/**
 * R230: Quest indicators над NPC — WotLK 3.3.5a Classic style.
 *
 *   ❗ ЖЁЛТЫЙ восклицательный  → есть НОВЫЙ квест (твой уровень подходит)
 *   ❗ СЕРЫЙ восклицательный   → есть НОВЫЙ квест, но уровень слишком низкий
 *   ❓ ЖЁЛТЫЙ вопросительный   → можно СДАТЬ квест (objective complete)
 *   ❓ СЕРЫЙ  вопросительный   → квест ВЗЯТ, но objective ещё в процессе
 *
 * Индикатор анимирован — bounce + glow. Пропадает когда квест сдан или
 * больше нет доступных у этого NPC. Distance cull = 80м.
 */

import { Matrix, Vector3 } from '@babylonjs/core';
import { useEffect, useState } from 'react';
import { useScene } from '../scene/Engine.js';
import { useNet } from '../state/net-store.js';
import { QUESTS } from '../data/quests.js';

const VIEW_DIST = 80;
const STORAGE_ACCEPTED = 'aiwari-quest-state:accepted';
const STORAGE_COMPLETED = 'aiwari-quest-state:completed';

interface AcceptedQuest {
  questId: string;
  progress: number;
  acceptedAt: number;
}

type MarkerKind = 'available-yellow' | 'available-grey' | 'turnin-yellow' | 'inprogress-grey';

interface QuestState {
  accepted: AcceptedQuest[];
  completed: string[];
}

function loadQuestState(): QuestState {
  try {
    const acc = JSON.parse(localStorage.getItem(STORAGE_ACCEPTED) ?? '[]') as AcceptedQuest[];
    const com = JSON.parse(localStorage.getItem(STORAGE_COMPLETED) ?? '[]') as string[];
    return { accepted: Array.isArray(acc) ? acc : [], completed: Array.isArray(com) ? com : [] };
  } catch {
    return { accepted: [], completed: [] };
  }
}

/** Decide which marker (if any) the giver-NPC should show right now. */
function pickMarker(
  giverId: string,
  state: QuestState,
  playerLevel: number,
  playerSide: string | null,
): MarkerKind | null {
  const giverQuests = QUESTS.filter((q) => q.giver === giverId);
  if (giverQuests.length === 0) return null;

  const acceptedSet = new Map(state.accepted.map((a) => [a.questId, a]));
  const completedSet = new Set(state.completed);

  // Highest priority: turn-in ready (objective complete on accepted quest).
  for (const q of giverQuests) {
    const acc = acceptedSet.get(q.id);
    if (!acc) continue;
    if (completedSet.has(q.id)) continue;
    const required = q.objective?.count ?? 1;
    if (acc.progress >= required) return 'turnin-yellow';
  }

  // Next: in-progress quest at this giver (player needs to come back).
  let hasInProgress = false;
  for (const q of giverQuests) {
    if (acceptedSet.has(q.id) && !completedSet.has(q.id)) {
      hasInProgress = true;
      break;
    }
  }

  // New available quest (not accepted, not completed, side+level match).
  let hasAvailableLevelOK = false;
  let hasAvailableLevelLow = false;
  for (const q of giverQuests) {
    if (acceptedSet.has(q.id)) continue;
    if (completedSet.has(q.id)) continue;
    // Side gate.
    if (q.giverSide && playerSide && q.giverSide !== playerSide) continue;
    // Prereq check (must complete all prereqs first).
    if (q.prereqs && q.prereqs.length > 0) {
      const ok = q.prereqs.every((id) => completedSet.has(id));
      if (!ok) continue;
    }
    if (playerLevel >= q.minLevel) hasAvailableLevelOK = true;
    else if (playerLevel >= q.minLevel - 5) hasAvailableLevelLow = true; // в диапазоне 5 уровней
  }

  if (hasAvailableLevelOK) return 'available-yellow';
  if (hasAvailableLevelLow) return 'available-grey';
  if (hasInProgress) return 'inprogress-grey';
  return null;
}

function styleFor(kind: MarkerKind): { glyph: string; color: string; shadow: string } {
  switch (kind) {
    case 'available-yellow':
      return { glyph: '!', color: '#C9A96A', shadow: 'rgba(251,191,36,0.85)' };
    case 'available-grey':
      return { glyph: '!', color: '#D1D5DB', shadow: 'rgba(209,213,219,0.65)' };
    case 'turnin-yellow':
      return { glyph: '?', color: '#C9A96A', shadow: 'rgba(251,191,36,0.85)' };
    case 'inprogress-grey':
      return { glyph: '?', color: '#9CA3AF', shadow: 'rgba(156,163,175,0.55)' };
  }
}

interface GiverInfo {
  id: string;
  pos: { x: number; z: number };
  // Quests are deduped server-side by giver id, but pos comes from first quest entry.
}

const GIVERS: GiverInfo[] = (() => {
  const seen = new Map<string, GiverInfo>();
  for (const q of QUESTS) {
    if (seen.has(q.giver)) continue;
    seen.set(q.giver, { id: q.giver, pos: { x: q.giverPos.x, z: q.giverPos.z } });
  }
  return Array.from(seen.values());
})();

export function NpcQuestMarkers() {
  const { scene } = useScene();
  const [, setTick] = useState(0);
  const [questState, setQuestState] = useState<QuestState>(() => loadQuestState());
  const players = useNet((s) => s.players);
  const selfId = useNet((s) => s.selfId);
  const self = selfId ? players[selfId] : null;

  // RAF tick для smooth marker projection.
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      setTick((t) => (t + 1) & 0xfff);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Reload localStorage quest state when quest events fire.
  useEffect(() => {
    const reload = () => setQuestState(loadQuestState());
    window.addEventListener('storage', reload);
    window.addEventListener('aiwari-accept-quest', reload as EventListener);
    window.addEventListener('aiwari-complete-quest', reload as EventListener);
    window.addEventListener('aiwari-quest-progress', reload as EventListener);
    // Periodic safety re-check каждые 2 сек.
    const id = window.setInterval(reload, 2000);
    return () => {
      window.removeEventListener('storage', reload);
      window.removeEventListener('aiwari-accept-quest', reload as EventListener);
      window.removeEventListener('aiwari-complete-quest', reload as EventListener);
      window.removeEventListener('aiwari-quest-progress', reload as EventListener);
      window.clearInterval(id);
    };
  }, []);

  if (!scene || !self) return null;
  const cam = scene.activeCamera;
  const canvas = scene.getEngine().getRenderingCanvas();
  if (!cam || !canvas) return null;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  const camPos = cam.position;
  const playerLevel = (self as { level?: number }).level ?? 1;
  const playerSide = ((self as { side?: string }).side ?? null) as string | null;

  return (
    <div className="absolute inset-0 z-25 pointer-events-none overflow-hidden">
      <style>{`
        @keyframes questBob {
          0%, 100% { transform: translate(-50%, -100%) translateY(0); }
          50%      { transform: translate(-50%, -100%) translateY(-4px); }
        }
        @keyframes questGlow {
          0%, 100% { filter: drop-shadow(0 0 4px var(--qsh)); }
          50%      { filter: drop-shadow(0 0 14px var(--qsh)); }
        }
      `}</style>
      {GIVERS.map((g) => {
        const dist = Math.hypot(camPos.x - g.pos.x, camPos.z - g.pos.z);
        if (dist > VIEW_DIST) return null;
        const kind = pickMarker(g.id, questState, playerLevel, playerSide);
        if (!kind) return null;
        const v3 = new Vector3(g.pos.x, 2.6, g.pos.z);
        const projected = Vector3.Project(
          v3,
          Matrix.Identity(),
          scene.getTransformMatrix(),
          cam.viewport.toGlobal(w, h),
        );
        if (projected.z < 0 || projected.z > 1) return null;
        const opacity = dist > 50 ? Math.max(0.35, 1 - (dist - 50) / 30) : 1;
        const s = styleFor(kind);
        return (
          <div
            key={g.id}
            style={{
              position: 'absolute',
              left: projected.x,
              top: projected.y,
              opacity,
              fontFamily: 'Cinzel, serif',
              fontWeight: 900,
              fontSize: 36,
              lineHeight: 1,
              color: s.color,
              ['--qsh' as never]: s.shadow,
              textShadow: `0 0 6px ${s.shadow}, 0 2px 4px rgba(0,0,0,0.85)`,
              animation: 'questBob 1.4s ease-in-out infinite, questGlow 1.8s ease-in-out infinite',
              willChange: 'transform, left, top',
              userSelect: 'none',
            }}
            title={
              kind === 'available-yellow'
                ? 'Доступный квест'
                : kind === 'available-grey'
                ? 'Квест выше твоего уровня'
                : kind === 'turnin-yellow'
                ? 'Можно сдать квест'
                : 'Квест в процессе'
            }
          >
            {s.glyph}
          </div>
        );
      })}
    </div>
  );
}
